import { useEffect, useState, useMemo } from 'react';
import { usersService, type User } from '../../services/users';

/**
 * Shared owner / assignee dropdown backed by /users/assignable.
 *
 * We deliberately store the user's display name (full_name or email)
 * as a plain string so the existing RAID schema (owner: String) stays
 * untouched. If/when the backend gains an owner_user_id column, callers
 * can switch by reading `findUser(value)` to resolve the User object.
 */

// Module-level cache so multiple OwnerSelects on the same page
// (and re-mounts via modal open/close) only hit the API once.
let userCache: User[] | null = null;
let inflight: Promise<User[]> | null = null;

async function loadUsers(): Promise<User[]> {
  if (userCache) return userCache;
  if (inflight) return inflight;
  inflight = usersService
    .assignable({ limit: 200 })
    .then((list) => {
      userCache = list;
      return list;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function invalidateOwnerCache() {
  userCache = null;
}

function displayName(u: User): string {
  return (u.full_name && u.full_name.trim()) || u.email;
}

interface OwnerSelectProps {
  /** Stored as the user's display name (full_name preferred, else email). */
  value: string;
  onChange: (value: string) => void;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  /** Label shown for the empty option. Defaults to "Unassigned". */
  placeholder?: string;
  className?: string;
  id?: string;
}

export function OwnerSelect({
  value,
  onChange,
  name = 'owner',
  required = false,
  disabled = false,
  placeholder = 'Unassigned',
  className,
  id,
}: OwnerSelectProps) {
  const [users, setUsers] = useState<User[]>(userCache ?? []);
  const [loading, setLoading] = useState(!userCache);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (userCache) {
      setUsers(userCache);
      setLoading(false);
      return;
    }
    setLoading(true);
    loadUsers()
      .then((list) => {
        if (!cancelled) {
          setUsers(list);
          setError(null);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not load users');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // If the persisted value isn't in the user list (e.g. a legacy free-text
  // owner from before this dropdown landed, or someone who left the org),
  // keep it selectable so editing the record doesn't silently clobber the
  // stored owner.
  const options = useMemo(() => {
    const names = users.map(displayName);
    const orphan = value && !names.includes(value) ? value : null;
    return { names, orphan };
  }, [users, value]);

  const baseClass =
    'w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500';

  return (
    <select
      id={id}
      name={name}
      value={value}
      required={required}
      disabled={disabled || loading}
      onChange={(e) => onChange(e.target.value)}
      className={className ?? baseClass}
    >
      <option value="">{loading ? 'Loading users…' : placeholder}</option>
      {options.orphan && (
        <option value={options.orphan}>{options.orphan} (not in org)</option>
      )}
      {users.map((u) => (
        <option key={u.id} value={displayName(u)}>
          {displayName(u)}
          {u.full_name ? ` · ${u.email}` : ''}
        </option>
      ))}
      {error && !loading && (
        <option value="" disabled>
          {error}
        </option>
      )}
    </select>
  );
}

export default OwnerSelect;
